import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  DollarIcon, FacebookIcon, InstagramIcon, RedditIcon, StarsIcon, TelegramIcon, TiktokIcon, XIcon,
} from "@signet/ui";
import { api, nowDay } from "@signet/web-kit";

/* ──────────────────────────────────────────────────────────────────────────
   One creator at a glance, beside her row in the quick bar (Jay, 2026-10-01):
   revenue and clicks today, her five busiest links and her five best paying
   platforms.

   How it reads, and why:

   - Two figures first, large, because they are the question ("how is she
     doing today"). They replace the agency's figures across the top while
     her pane is open, so two different "Clicks today" are never on screen.
   - Then two ranked lists, each one row per item on a single line: the
     name, what it is in muted text, a bar, and the number. A bar per row
     because the job is comparing sizes, and a bar shows "this one is twice
     that one" faster than digits do; the number stays beside it for the
     exact figure. One colour per list (clicks in the accent, revenue in
     violet, as on Now), since the rows are ranks of one measure and not
     different series.
   - Colour never carries meaning alone: a link's category is a dot in the
     category's own colour next to its name, written out, and every bar has
     its number.

   Each half needs its own permission; without either, the pane is not drawn.
   ────────────────────────────────────────────────────────────────────────── */

type LinksToday = {
  day: string;
  found: boolean;
  clicks: number;
  linkCount: number;
  links: { id: string; label: string; address: string; clicks: number; category: { name: string; color: string } | null; pageType: string }[];
};

type Platform = "instagram" | "reddit" | "telegram" | "facebook" | "twitter" | "tiktok" | "sfs";
type Latest =
  | { available: false; currency: string }
  | {
      available: true;
      currency: string;
      found: boolean;
      from: string;
      to: string;
      stale: boolean;
      total: number;
      dashboards: { name: string; platform: Platform | null; earnings: number }[];
      more: number;
    };

// SFS is stars, as on Now: creators promoting each other rather than a platform.
const PLATFORM_ICON: Record<Platform, typeof InstagramIcon> = {
  instagram: InstagramIcon,
  reddit: RedditIcon,
  telegram: TelegramIcon,
  facebook: FacebookIcon,
  twitter: XIcon,
  tiktok: TiktokIcon,
  sfs: StarsIcon,
};

/** Whole units once a figure is big enough that cents are noise. */
function cash(value: number, currency: string) {
  const big = Math.abs(value) >= 100;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, currencyDisplay: "narrowSymbol", minimumFractionDigits: big ? 0 : 2, maximumFractionDigits: big ? 0 : 2 }).format(value);
  } catch {
    return `$${value.toFixed(big ? 0 : 2)}`;
  }
}

const days = (from: string, to: string) => (from === to ? nowDay(to) : `${nowDay(from)} to ${nowDay(to)}`);

/** The name only settles once the arrows stop, so running down the list asks for one creator, not ten. */
function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export function CreatorDetail({ name, canClicks, canRevenue }: { name: string; canClicks: boolean; canRevenue: boolean }) {
  const settled = useSettled(name, 120);
  const ready = settled === name;
  const links = useQuery({
    queryKey: ["quick", "creator-links", settled],
    queryFn: () => api<LinksToday>(`/api/m/links/creator-today?name=${encodeURIComponent(settled)}`),
    enabled: canClicks && Boolean(settled),
    staleTime: 60_000,
  });
  const latest = useQuery({
    queryKey: ["quick", "creator-latest", settled],
    queryFn: () => api<Latest>(`/api/m/reporting/creator-latest?name=${encodeURIComponent(settled)}`),
    enabled: canRevenue && Boolean(settled),
    staleTime: 60_000,
  });

  const l = ready ? links.data : undefined;
  const r = ready ? latest.data : undefined;
  const revenue = r && r.available ? r : null;

  return (
    <div className="signet-qb-detail-pane" aria-label={`${name} today`}>
      <div className="signet-qb-kpis">
        {canRevenue ? (
          <Figure
            label={revenue ? `Revenue, ${days(revenue.from, revenue.to)}` : "Revenue, latest day"}
            value={revenue ? (revenue.found ? cash(revenue.total, revenue.currency) : null) : null}
            empty={r ? (!r.available ? "No figures yet" : !r.found ? "Not in Reporting" : null) : null}
            loading={!r && !latest.isError}
            failed={latest.isError}
            chip={revenue?.stale ? "Over a day old" : null}
            tone="violet"
          />
        ) : null}
        {canClicks ? (
          <Figure
            label="Clicks today"
            value={l?.found ? l.clicks.toLocaleString() : null}
            empty={l && !l.found ? "Not in Links" : null}
            note={l?.found && l.linkCount ? `across ${l.linkCount} ${l.linkCount === 1 ? "link" : "links"}` : null}
            loading={!l && !links.isError}
            failed={links.isError}
            tone="accent"
          />
        ) : null}
      </div>

      {canClicks ? (
        <Ranked
          title="Top links today"
          unit="clicks"
          tone="accent"
          loading={!l && !links.isError}
          empty={l ? (!l.found ? "Links has no creator by this name." : l.links.length ? null : "No clicks yet today.") : links.isError ? "Couldn't load her links." : null}
          rows={(l?.links ?? []).map((link) => ({
            key: link.id,
            name: link.label,
            title: link.address,
            meta: (
              <>
                {link.category ? (
                  <span className="signet-qb-cat">
                    <i style={{ background: link.category.color }} aria-hidden="true" />
                    {link.category.name}
                  </span>
                ) : (
                  <span className="signet-qb-cat signet-qb-none">No category</span>
                )}
                <span className="signet-qb-kind">{link.pageType}</span>
              </>
            ),
            value: link.clicks,
            shown: link.clicks.toLocaleString(),
          }))}
        />
      ) : null}

      {canRevenue ? (
        <Ranked
          title={revenue ? `Earnings by platform, ${days(revenue.from, revenue.to)}` : "Earnings by platform"}
          unit="earned"
          tone="violet"
          loading={!r && !latest.isError}
          empty={
            r
              ? !r.available
                ? "No figures yet."
                : !r.found
                  ? "Reporting has no creator by this name."
                  : r.dashboards.length
                    ? null
                    : "Nothing earned on the latest day."
              : latest.isError
                ? "Couldn't load her earnings."
                : null
          }
          more={revenue?.more ? `and ${revenue.more} more` : null}
          rows={(revenue?.dashboards ?? []).map((dashboard) => {
            const Icon = dashboard.platform ? PLATFORM_ICON[dashboard.platform] : DollarIcon;
            return {
              key: dashboard.name,
              name: dashboard.name,
              icon: <Icon size={13} />,
              value: dashboard.earnings,
              shown: cash(dashboard.earnings, revenue!.currency),
            };
          })}
        />
      ) : null}
    </div>
  );
}

function Figure({ label, value, empty, note, chip, loading, failed, tone }: {
  label: string;
  value: string | null;
  empty?: string | null;
  note?: string | null;
  chip?: string | null;
  loading: boolean;
  failed: boolean;
  tone: "accent" | "violet";
}) {
  return (
    <div className={`signet-qb-kpi ${tone}`}>
      <span className="signet-qb-kpi-label">
        {label}
        {chip ? <span className="signet-qb-chip">{chip}</span> : null}
      </span>
      {failed ? (
        <span className="signet-qb-kpi-empty">Couldn't load this</span>
      ) : loading ? (
        <span className="signet-qb-kpi-loading" aria-label="Loading" />
      ) : value !== null ? (
        <span className="signet-qb-kpi-value">{value}</span>
      ) : (
        <span className="signet-qb-kpi-empty">{empty ?? "Nothing yet"}</span>
      )}
      {note && !failed && !loading ? <span className="signet-qb-kpi-note">{note}</span> : null}
    </div>
  );
}

type RankedRow = { key: string; name: string; title?: string; icon?: React.ReactNode; meta?: React.ReactNode; value: number; shown: string };

function Ranked({ title, unit, tone, rows, loading, empty, more }: {
  title: string;
  unit: string;
  tone: "accent" | "violet";
  rows: RankedRow[];
  loading: boolean;
  empty: string | null;
  more?: string | null;
}) {
  // Bars share one scale within a list, from zero, so lengths compare honestly.
  const top = Math.max(0, ...rows.map((row) => row.value));
  return (
    <section className={`signet-qb-ranked ${tone}`}>
      <header>
        <span>{title}</span>
        <span>{unit}</span>
      </header>
      {loading ? (
        <div className="signet-qb-ranked-loading" aria-label="Loading">
          {[0, 1, 2].map((i) => <span key={i} />)}
        </div>
      ) : empty ? (
        <p className="signet-qb-ranked-empty">{empty}</p>
      ) : (
        <ol>
          {rows.map((row) => (
            <li key={row.key} title={row.title ? `${row.name}\n${row.title}` : row.name}>
              <span className="signet-qb-ranked-name">
                {row.icon ? <span className="signet-qb-ranked-icon" aria-hidden="true">{row.icon}</span> : null}
                <span className="signet-qb-ranked-text">{row.name}</span>
              </span>
              {row.meta ? <span className="signet-qb-ranked-meta">{row.meta}</span> : null}
              <span className="signet-qb-bar" aria-hidden="true">
                <i style={{ width: `${top > 0 ? Math.max(3, (Math.max(0, row.value) / top) * 100) : 0}%` }} />
              </span>
              <span className="signet-qb-ranked-value">{row.shown}</span>
            </li>
          ))}
        </ol>
      )}
      {more && !loading && !empty ? <p className="signet-qb-ranked-more">{more}</p> : null}
    </section>
  );
}

